// @core/stores/localization/LocalizationStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { localizationApi } from '@core/api';
import type {
  ApproveTranslationRequest,
  BulkExportTranslationsResponse,
  BulkImportTranslationsRequest,
  BulkImportTranslationsResponse,
  CreateGlossaryEntryRequest,
  CreateLocaleRequest,
  CreateNamespaceRequest,
  CreateTranslationKeyRequest,
  GlossaryEntryResponse,
  ListGlossaryEntriesResponse,
  ListNamespacesResponse,
  ListTranslationKeysResponse,
  ListTranslationsResponse,
  Locale,
  NamespaceResponse,
  PlatformLocaleConfigResponse,
  ReviewTranslationRequest,
  SetTranslationRequest,
  TranslationBundleResponse,
  TranslationKeyResponse,
  TranslationResponse,
  TranslationStatsResponse,
  UpdateGlossaryEntryRequest,
  UpdateLocaleRequest,
  UpdatePlatformLocaleConfigRequest,
  UpdateTranslationKeyRequest,
} from '@core/types/localization';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class LocalizationStore {
  // List state
  items: Locale[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: Locale | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, Locale>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): Locale | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: Locale | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }
  private removeFromCache(id: string): void {
    this.cache.delete(id);
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Approve translation
   */
  async ApproveTranslation(translation_id: string, data: ApproveTranslationRequest): Promise<TranslationResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await localizationApi.ApproveTranslation(translation_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Bulk export translations
   */
  async BulkExportTranslations(params?: { }): Promise<BulkExportTranslationsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await localizationApi.BulkExportTranslations(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Bulk import translations
   */
  async BulkImportTranslations(data: BulkImportTranslationsRequest): Promise<BulkImportTranslationsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await localizationApi.BulkImportTranslations(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Create glossary entry
   */
  async CreateGlossaryEntry(data: CreateGlossaryEntryRequest): Promise<GlossaryEntryResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await localizationApi.CreateGlossaryEntry(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Create a locale
   */
  async CreateLocale(data: CreateLocaleRequest): Promise<Locale> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await localizationApi.CreateLocale(data);
      const item = response.locale;

      runInAction(() => {
        if (item) {
          this.items = [item, ...this.items];
          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * Create translation namespace
   */
  async CreateNamespace(data: CreateNamespaceRequest): Promise<NamespaceResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await localizationApi.CreateNamespace(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Create translation key
   */
  async CreateTranslationKey(data: CreateTranslationKeyRequest): Promise<TranslationKeyResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await localizationApi.CreateTranslationKey(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Delete glossary entry
   */
  async DeleteGlossaryEntry(entry_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await localizationApi.DeleteGlossaryEntry(entry_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== entry_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === entry_id) {
          this.currentItem = null;
        }

        this.removeFromCache(entry_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Delete locale
   */
  async DeleteLocale(locale_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await localizationApi.DeleteLocale(locale_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== locale_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === locale_id) {
          this.currentItem = null;
        }

        this.removeFromCache(locale_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Delete translation key
   */
  async DeleteTranslationKey(key_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await localizationApi.DeleteTranslationKey(key_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== key_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === key_id) {
          this.currentItem = null;
        }

        this.removeFromCache(key_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Get locale
   */
  async GetLocale(locale_id: string): Promise<Locale> {
    // Check cache first
    const cached = this.cache.get(locale_id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await localizationApi.GetLocale(locale_id);
      const item = response.locale;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * Get missing translations
   */
  async GetMissingTranslations(params?: { locale_code?: string | number | boolean; }): Promise<ListTranslationKeysResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await localizationApi.GetMissingTranslations(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get translation namespace
   */
  async GetNamespace(namespace_id: string): Promise<NamespaceResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await localizationApi.GetNamespace(namespace_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get platform locale config
   */
  async GetPlatformLocaleConfig(params?: { }): Promise<PlatformLocaleConfigResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await localizationApi.GetPlatformLocaleConfig(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get translation
   */
  async GetTranslation(translation_id: string): Promise<TranslationResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await localizationApi.GetTranslation(translation_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get translation bundle
   */
  async GetTranslationBundle(locale_code: string, namespace_slug: string): Promise<TranslationBundleResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await localizationApi.GetTranslationBundle(locale_code, namespace_slug);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get translation stats
   */
  async GetTranslationStats(params?: { }): Promise<TranslationStatsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await localizationApi.GetTranslationStats(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List glossary entries
   */
  async ListGlossaryEntries(params?: PaginatedRequest): Promise<ListGlossaryEntriesResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await localizationApi.ListGlossaryEntries(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List locales
   */
  async ListLocales(params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await localizationApi.ListLocales(params);

      runInAction(() => {
        this.items = response.items?.items ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List translation namespaces
   */
  async ListNamespaces(params?: PaginatedRequest): Promise<ListNamespacesResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await localizationApi.ListNamespaces(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List translation keys
   */
  async ListTranslationKeys(params?: PaginatedRequest): Promise<ListTranslationKeysResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await localizationApi.ListTranslationKeys(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List translations
   */
  async ListTranslations(params?: PaginatedRequest): Promise<ListTranslationsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await localizationApi.ListTranslations(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Review translation
   */
  async ReviewTranslation(translation_id: string, data: ReviewTranslationRequest): Promise<TranslationResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await localizationApi.ReviewTranslation(translation_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Search translation keys
   */
  async SearchTranslationKeys(params?: { limit?: string | number | boolean; cursor?: string | number | boolean; direction?: string | number | boolean; sort?: string | number | boolean; }): Promise<ListTranslationKeysResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await localizationApi.SearchTranslationKeys(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Set translation
   */
  async SetTranslation(data: SetTranslationRequest): Promise<TranslationResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await localizationApi.SetTranslation(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update glossary entry
   */
  async UpdateGlossaryEntry(entry_id: string, data: UpdateGlossaryEntryRequest): Promise<GlossaryEntryResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await localizationApi.UpdateGlossaryEntry(entry_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update locale
   */
  async UpdateLocale(locale_id: string, data: UpdateLocaleRequest): Promise<Locale> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await localizationApi.UpdateLocale(locale_id, data);
      const item = response.locale;

      runInAction(() => {
        if (item) {
          // Update in list
          const index = this.items.findIndex(i => i.entity?.id === item.entity?.id);
          if (index !== -1) {
            this.items[index] = item;
          }

          // Update current item if same
          if (this.currentItem?.entity?.id === item.entity?.id) {
            this.currentItem = item;
          }

          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * Update platform locale config
   */
  async UpdatePlatformLocaleConfig(data: UpdatePlatformLocaleConfigRequest): Promise<PlatformLocaleConfigResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await localizationApi.UpdatePlatformLocaleConfig(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update translation key
   */
  async UpdateTranslationKey(key_id: string, data: UpdateTranslationKeyRequest): Promise<TranslationKeyResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await localizationApi.UpdateTranslationKey(key_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }
}

export const localizationStore = new LocalizationStore();
